import { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage, useSEO } from "../context/LanguageContext";
import { PageHero, SectionHeading, Reveal, Overline } from "../components/Kinetic";
import { NewsletterForm } from "../components/SubmissionForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActualitesPage = () => {
  const { t, lang } = useLanguage();
  useSEO(t("actualites.metaTitle"), t("actualites.metaDesc"));
  const themes = t("actualites.themes");
  const [newsItems, setNewsItems] = useState(null);

  useEffect(() => {
    axios.get(`${API}/news`).then((r) => setNewsItems(r.data)).catch(() => setNewsItems([]));
  }, []);

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="actualites-page">
      <PageHero
        overline={t("actualites.overline")}
        titleLines={t("actualites.titleLines")}
        testid="actualites"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          {newsItems && newsItems.length > 0 ? (
            <div className="space-y-6" data-testid="actualites-list">
              {newsItems.map((n, i) => (
                <Reveal key={n.id} delay={i * 0.06}>
                  <article className="border border-graphite/15 bg-white p-8 md:p-12" data-testid={`news-item-${n.id}`}>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-forest">
                      {t(`newsCats.${n.category}`)} — {fmtDate(n.created_at)}
                    </p>
                    {n.agent_generated && (
                      <p className="mt-2 inline-block border border-science/30 bg-science-soft/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-science" data-testid={`news-agent-badge-${n.id}`}>
                        {t("newsAgent.badge")}
                      </p>
                    )}
                    <h2 className="mt-4 font-display text-2xl md:text-4xl font-light tracking-tight text-graphite">
                      {lang === "en" && n.title_en ? n.title_en : n.title_fr}
                    </h2>
                    {(lang === "en" && n.summary_en ? n.summary_en : n.summary_fr) && (
                      <p className="mt-3 max-w-3xl text-base font-medium text-graphite">
                        {lang === "en" && n.summary_en ? n.summary_en : n.summary_fr}
                      </p>
                    )}
                    <p className="mt-4 max-w-3xl whitespace-pre-wrap text-base leading-relaxed text-graphite-soft">
                      {lang === "en" && n.content_en ? n.content_en : n.content_fr}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>
          ) : (
            <Reveal>
              <div className="border border-graphite/15 bg-white p-10 md:p-16" data-testid="actualites-empty-state">
                <Overline>{t("status.prep")}</Overline>
                <h2 className="mt-5 max-w-3xl font-display text-3xl md:text-5xl font-light tracking-tight leading-tight text-graphite">
                  {t("actualites.emptyTitle")}
                </h2>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-graphite-soft">
                  {t("actualites.emptyText")}
                </p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <section className="border-t border-graphite/10 bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading overline={t("actualites.themesOverline")} title={t("actualites.themesTitle")} />
          <div className="mt-14 flex flex-wrap gap-3">
            {themes.map((theme, i) => (
              <Reveal key={theme} delay={i * 0.04} y={12}>
                <span className="inline-block border border-graphite/15 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-graphite-soft transition-colors duration-200 hover:border-forest hover:text-forest">
                  {theme}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-graphite/10 py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            overline={t("actualites.sourcesOverline")}
            title={t("actualites.sourcesTitle")}
            text={t("actualites.sourcesText")}
          />
          <div className="mt-14 grid gap-px bg-graphite/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="actualites-sources">
            {t("actualites.sources").map((s, i) => (
              <Reveal key={s.name} delay={i * 0.04} className="h-full">
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex h-full flex-col justify-between bg-bone p-7 transition-colors duration-300 hover:bg-science"
                  data-testid={`source-link-${i}`}
                >
                  <div>
                    <p className="font-mono text-xs text-science transition-colors duration-300 group-hover:text-white/60">
                      {String(i + 1).padStart(2, "0")}
                    </p>
                    <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-tight text-graphite transition-colors duration-300 group-hover:text-white">
                      {s.name}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-graphite-soft transition-colors duration-300 group-hover:text-white/70">
                      {s.desc}
                    </p>
                  </div>
                  <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.12em] text-graphite-soft/60 transition-colors duration-300 group-hover:text-white/70">
                    {s.url.replace("https://", "")}
                  </p>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-graphite py-24 text-white md:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2 lg:items-center">
          <div>
            <Reveal y={16}>
              <Overline light>Newsletter</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl md:text-4xl font-light tracking-tight">
                {t("actualites.newsletterTitle")}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60">{t("actualites.newsletterText")}</p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <NewsletterForm context="actualites" dark />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ActualitesPage;
